// The profile doc doubles as the home for cross-device app STATE (visit
// history, tour/checklist flags) and per-document bookkeeping (gcs_url,
// raw_content). Those writes can create or outlive the doc without the
// student having told us anything about themselves — a "phantom profile"
// (meta-only doc) or a "husk" (every sourced field nulled by document
// deletion, bookkeeping remnants left behind). Both made the app render an
// empty all-N/A Student card, skip the first-login tour, and check off
// "Tell us about you".
//
// Rule: a profile is REAL only if at least one non-bookkeeping field holds
// an actual value. Keys alone don't count — deletion nulls values in place.

export const META_PROFILE_FIELDS = new Set([
    'visited_sections',
    'quick_tour_seen',
    'onboarding_checklist_dismissed',
    'weekly_digest_opt_out',
    'visit_reminder_opt_out',
    'created_at',
    'updated_at',
    'indexed_at',
    'user_id',
    'content_type',
    'field_sources',
    // per-document bookkeeping — describes uploads, not the student
    'raw_content',
    'original_filename',
    'gcs_url',
    'file_size',
    'upload_date',
    'uploaded_files',
]);

function isRealValue(v) {
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true; // 0 and false are information, not absence
}

export function hasRealProfileData(profile) {
    if (!profile || typeof profile !== 'object') return false;
    return Object.entries(profile).some(
        ([k, v]) => !META_PROFILE_FIELDS.has(k) && isRealValue(v)
    );
}

// A profile can be "real" (hasRealProfileData) purely on activities/awards
// while having zero transcript signal — a 9th grader who just joined a club.
// Fit odds are academics-anchored (CDS factor weighting), so any surface
// offering to check odds against the KB needs THIS check, not the broader
// one, or it invites a freshman with nothing gradable yet to "see their
// odds" against schools with no real signal behind the read.
export function hasAcademicSignal(profile) {
    if (!profile || typeof profile !== 'object') return false;
    if (Array.isArray(profile.courses) && profile.courses.length > 0) return true;
    return Object.entries(profile).some(
        ([k, v]) => k.startsWith('gpa_') && v !== null && v !== undefined && v !== '' && v !== 0
    );
}

function namedItems(xs) {
    return Array.isArray(xs) && xs.some((x) => x && (x.name || x.activity || x.title || typeof x === 'string'));
}

// Same 1-of-3 bar ActivityIdeasPanel already uses (intended major, courses,
// or extracurriculars) — a first-semester freshman with one club and no
// grades yet clears THIS bar well before Major Map's 2-of-3 one. Kept in
// sync with frontend/src/components/majors/ActivityIdeasPanel.jsx by hand;
// no shared import path exists between the two today.
export function hasDirectionSignal(profile) {
    if (!profile || typeof profile !== 'object') return false;
    if (profile.intended_major) return true;
    if (Array.isArray(profile.intended_majors) && profile.intended_majors.length > 0) return true;
    return namedItems(profile.extracurriculars) || namedItems(profile.courses);
}

// Narrower than hasDirectionSignal on purpose (founder, 2026-08-18: the
// story wizard "should not come up for existing users who have already
// specified their interested major and picked colleges") — a freshman with
// one club listed but no chosen major still needs the story wizard; a
// freshman who's already named a major has moved past the question it asks.
export function hasChosenMajor(profile) {
    if (!profile || typeof profile !== 'object') return false;
    if (profile.intended_major) return true;
    return Array.isArray(profile.intended_majors) && profile.intended_majors.length > 0;
}
